import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Mail } from "lucide-react";
import { api, invoicePdfUrl } from "@/lib/api";
import { localeFor } from "@/lib/money";

// Every bill on a sale (one per payment), newest first: open the PDF, see whether it reached the
// member, and email one again (e.g. after fixing a typo in their address).
export default function BillHistory({ kind = "sale", id }) {
  const [rows, setRows] = useState(null);
  const [sending, setSending] = useState(null);
  const load = useCallback(() => {
    api.get("/invoices", { params: { kind, id } }).then((r) => setRows(r.data.invoices || [])).catch(() => setRows([]));
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  const again = async (inv) => {
    const to = window.prompt(`Email bill ${inv.number} to:`, inv.buyer?.email || "");
    if (to === null) return;
    setSending(inv.id);
    try {
      const r = await api.post(`/invoices/${inv.id}/email`, { to: to.trim() });
      toast.success(`Bill ${inv.number} emailed to ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The email didn't go out");
    } finally { setSending(null); load(); }
  };

  if (rows === null) return null;
  const amount = (inv) => `${inv.currency || ""} ${new Intl.NumberFormat(localeFor(inv.currency), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(inv.total || 0)}`;
  return (
    <div className="rounded-lg border border-[#262626] p-3 space-y-2" data-testid="bill-history">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Bills <span className="normal-case tracking-normal text-zinc-500">· one per payment, emailed about 10 minutes after it's recorded</span></div>
      {rows.length === 0 && <div className="text-xs text-zinc-500">No bills yet.</div>}
      {rows.map((inv) => {
        const sent = (inv.emails || []).slice(-1)[0];
        return (
          <div key={inv.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`bill-history-${inv.id}`}>
            <span className="font-mono text-xs text-zinc-200">{inv.number}</span>
            <span className="text-xs text-zinc-500 tabular-nums">{inv.issued_on}</span>
            <span className="text-xs text-zinc-400">{inv.package?.label || "Whole package"}</span>
            <span className="font-mono text-xs">{amount(inv)}</span>
            {sent
              ? <span className="text-[11px] text-emerald-300" title={sent.at}>emailed to {sent.to}</span>
              : <span className="text-[11px] text-rose-300" title={inv.email_error || ""}>{inv.email_error ? "email didn't go out" : "not emailed"}</span>}
            <span className="ml-auto flex items-center gap-3">
              <a href={invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 text-xs inline-flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" /> PDF</a>
              <button type="button" onClick={() => again(inv)} disabled={sending === inv.id}
                className="text-zinc-400 hover:text-zinc-200 text-xs inline-flex items-center gap-1" data-testid="bill-history-email">
                {sending === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                {sent ? "Email again" : "Email"}</button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
